import api from '../utils/api';

/**
 * Service for managing user notifications
 */
const notificationService = {
  /**
   * Get all notifications for the authenticated user
   * @param {Object} params - Query parameters (limit, offset)
   * @returns {Promise} - API response with notifications
   */
  getNotifications: async (params = {}) => {
    try {
      const response = await api.get('/notifications', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch notifications' };
    }
  },

  /**
   * Mark a notification as read
   * @param {string} id - Notification ID
   * @returns {Promise} - API response
   */
  markAsRead: async (id) => {
    try {
      const response = await api.put(`/notifications/${id}/read`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to mark notification as read' };
    }
  },

  /**
   * Mark all notifications as read
   * @returns {Promise} - API response
   */
  markAllAsRead: async () => {
    try {
      const response = await api.put('/notifications/read-all');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to mark all notifications as read' };
    }
  },

  /**
   * Delete a notification
   * @param {string} id - Notification ID
   * @returns {Promise} - API response
   */
  deleteNotification: async (id) => {
    try {
      const response = await api.delete(`/notifications/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete notification' };
    }
  },

  /**
   * Delete all notifications
   * @returns {Promise} - API response
   */
  deleteAllNotifications: async () => {
    try {
      const response = await api.delete('/notifications/all');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete all notifications' };
    }
  },

  /**
   * Create a system notification (admin only)
   * @param {Object} notificationData - Notification data
   * @returns {Promise} - API response
   */
  createSystemNotification: async (notificationData) => {
    try {
      const response = await api.post('/notifications/system', notificationData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create system notification' };
    }
  },

  /**
   * Create a broadcast notification (admin only)
   * @param {Object} notificationData - Notification data
   * @returns {Promise} - API response
   */
  createBroadcastNotification: async (notificationData) => {
    try {
      const response = await api.post('/notifications/broadcast', notificationData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create broadcast notification' };
    }
  }
};

export default notificationService;
